import { Controller } from "@hotwired/stimulus"

// Challenge picker palette on the editorial pick form — a focused adaptation
// of search_modal_controller's server mode. Debounces typing into the
// "challenge_search_results" Turbo Frame, adds mode-filter chips (the extra
// `type` param search_modal doesn't know about), and keeps ↑/↓ + Enter
// navigation over the loaded rows. Picking a row is plain link navigation
// back into the form with ?competition_id=.
export default class extends Controller<HTMLElement> {
  static targets = ["dialog", "input", "result", "chip"]
  static values = { url: String }

  declare readonly dialogTarget: HTMLDialogElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly resultTargets: HTMLAnchorElement[]
  declare readonly chipTargets: HTMLButtonElement[]
  declare readonly urlValue: string

  private debounce?: number
  private activeIndex = -1
  private type = ""

  disconnect() {
    if (this.debounce) window.clearTimeout(this.debounce)
  }

  open() {
    if (this.dialogTarget.open) return
    this.dialogTarget.showModal()
    this.inputTarget.value = ""
    this.activeIndex = -1
    this.loadResults("")
    // Focus after the dialog paints so the cursor lands in the field.
    requestAnimationFrame(() => this.inputTarget.focus())
  }

  close() {
    if (this.dialogTarget.open) this.dialogTarget.close()
  }

  onClose() {
    if (this.debounce) window.clearTimeout(this.debounce)
  }

  // Close only on a true backdrop click (event.target === dialog element).
  backdropClose(event: MouseEvent) {
    if (event.target === this.dialogTarget) this.close()
  }

  search() {
    if (this.debounce) window.clearTimeout(this.debounce)
    this.debounce = window.setTimeout(() => this.loadResults(this.inputTarget.value), 160)
  }

  setType(event: Event) {
    const chip = event.currentTarget as HTMLButtonElement
    this.type = chip.dataset.type || ""
    this.chipTargets.forEach((c) => c.classList.toggle("is-active", c === chip))
    this.loadResults(this.inputTarget.value)
    this.inputTarget.focus()
  }

  onResults() {
    this.activeIndex = -1
    this.clearActive()
  }

  onKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.move(-1)
        break
      case "Enter": {
        const active = this.resultTargets[this.activeIndex]
        if (active) {
          event.preventDefault()
          active.click()
        }
        break
      }
      case "Escape":
        // type="search" inputs swallow the first Escape to clear themselves;
        // close the dialog ourselves so one press always dismisses it.
        event.preventDefault()
        this.close()
        break
    }
  }

  private loadResults(query: string) {
    const frame = this.element.querySelector<HTMLElement>("turbo-frame#challenge_search_results")
    if (!frame) return
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", query.trim())
    if (this.type !== "") url.searchParams.set("type", this.type)
    frame.setAttribute("src", url.toString())
  }

  private move(delta: number) {
    const results = this.resultTargets
    if (results.length === 0) return
    this.activeIndex = (this.activeIndex + delta + results.length) % results.length
    this.clearActive()
    const active = results[this.activeIndex]
    active.classList.add("is-active")
    active.scrollIntoView({ block: "nearest" })
  }

  private clearActive() {
    this.resultTargets.forEach((el) => el.classList.remove("is-active"))
  }
}
